// Wishlist.jsx — Saved wishlist page, reads from localStorage

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase/config";
import ProductCard from "../components/ProductCard";

// MUI Icons
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";

const WISHLIST_KEY = "giftcraft_wishlist";

function getWishlistIds() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY) || "[]");
  } catch {
    return [];
  }
}

function ProductSkeleton() {
  return (
    <div className="bg-white rounded-3xl overflow-hidden border border-gray-100" style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
      <div className="skeleton" style={{ height: "220px" }} />
      <div className="p-4 space-y-3">
        <div className="skeleton h-3 rounded-full w-1/3" />
        <div className="skeleton h-4 rounded-full w-3/4" />
        <div className="skeleton h-3 rounded-full w-full" />
        <div className="skeleton h-3 rounded-full w-2/3" />
        <div className="flex justify-between items-center pt-2">
          <div className="skeleton h-5 rounded-full w-16" />
          <div className="skeleton h-8 rounded-xl w-24" />
        </div>
      </div>
    </div>
  );
}

export default function Wishlist() {
  const [allProducts, setAllProducts] = useState([]);
  const [wishlistIds, setWishlistIds] = useState(getWishlistIds);
  const [loading, setLoading] = useState(true);
  const [showClearModal, setShowClearModal] = useState(false);

  // Sync wishlist IDs when localStorage changes (e.g. items removed from ProductCard)
  useEffect(() => {
    const sync = () => setWishlistIds(getWishlistIds());
    window.addEventListener("wishlist_updated", sync);
    return () => window.removeEventListener("wishlist_updated", sync);
  }, []);

  // Fetch all products once
  useEffect(() => {
    async function fetchProducts() {
      try {
        const q = query(collection(db, "products"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        setAllProducts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Wishlist: error fetching products", err);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  const wishlistProducts = allProducts.filter((p) => wishlistIds.includes(p.id));

  const confirmClearAll = useCallback(() => {
    localStorage.removeItem(WISHLIST_KEY);
    window.dispatchEvent(new Event("wishlist_updated"));
    setWishlistIds([]);
    setShowClearModal(false);
  }, []);

  return (
    <div className="page-enter min-h-screen" style={{ backgroundColor: "#FFFBF8" }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                <Link to="/" className="hover:text-rose-500 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-gray-700 font-medium">Wishlist</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
                  <FavoriteIcon sx={{ fontSize: 22, color: "white" }} />
                </div>
                <div>
                  <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                    My Wishlist
                  </h1>
                  <p className="text-gray-500 text-sm mt-0.5">
                    {loading
                      ? "Loading saved items…"
                      : `${wishlistIds.length} saved item${wishlistIds.length !== 1 ? "s" : ""}`}
                  </p>
                </div>
              </div>
            </div>

            {wishlistIds.length > 0 && !loading && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowClearModal(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-rose-600 border border-gray-200 hover:border-rose-200 px-4 py-2.5 rounded-xl transition-all"
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                Clear All
              </motion.button>
            )}
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => <ProductSkeleton key={i} />)}
          </div>
        ) : wishlistIds.length === 0 ? (
          /* Empty state */
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center justify-center py-24 text-center"
          >
            <div className="w-24 h-24 rounded-full bg-rose-50 flex items-center justify-center mb-6 shadow-inner">
              <FavoriteBorderOutlinedIcon sx={{ fontSize: 48, color: "#f0155a" }} />
            </div>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">Your wishlist is empty</h2>
            <p className="text-gray-500 text-base mb-8 max-w-xs">
              Browse our collection and tap the heart icon on any product to save it here.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold px-7 py-3.5 rounded-2xl hover:opacity-90 transition shadow-lg text-sm"
              >
                <CardGiftcardOutlinedIcon sx={{ fontSize: 18 }} />
                Browse Products
              </Link>

            </div>
          </motion.div>
        ) : (
          <>
            {/* Product grid */}
            <AnimatePresence>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {wishlistProducts.map((p, i) => (
                  <motion.div
                    key={p.id}
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
                  >
                    <ProductCard product={p} index={i} />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Continue shopping CTA */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-14 rounded-3xl overflow-hidden relative text-center px-8 py-12"
              style={{ background: "linear-gradient(135deg, #fff1f4 0%, #fdf4ff 50%, #fff8f0 100%)" }}
            >
              <h3 className="font-display text-2xl font-bold text-gray-900 mb-2">
                Ready to checkout?
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                Add your favourites to the cart and complete your order.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/cart"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold px-7 py-3.5 rounded-2xl hover:opacity-90 transition shadow-lg text-sm"
                >
                  <ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />
                  Go to Cart
                </Link>
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 bg-white text-gray-700 font-semibold border border-gray-200 px-7 py-3.5 rounded-2xl hover:border-rose-300 hover:text-rose-600 transition text-sm"
                >
                  Keep Browsing <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </div>

      {/* Clear Wishlist Confirmation Modal */}
      <AnimatePresence>
        {showClearModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 overflow-hidden"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Clear Wishlist?</h3>
              <p className="text-gray-600 text-sm mb-6">
                Are you sure you want to remove all items from your wishlist? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3 font-semibold text-sm">
                <button
                  onClick={() => setShowClearModal(false)}
                  className="px-5 py-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmClearAll}
                  className="px-5 py-2.5 rounded-xl text-white bg-rose-600 hover:bg-rose-700 transition-colors shadow-sm"
                >
                  Clear All
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
